import React, {useState, useEffect, useMemo, useCallback} from 'react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';

import ALink from '../components/common/ALink';
import Qty from '../components/partials/product/qty';
import { actions as CartAction } from '../store/cart/actions';
import { getCartTotal, getSaveTotal } from '../utils';
import CartAlertModal from '../components/features/modals/cartAlertModal';
import { useAuthModalStore } from '../store/auth-modal';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { isEmpty } from 'lodash';
import { sections } from '../utils/data/shop';
import { useRouter } from 'next/router';
import MetaHeader from '../components/common/MetaHeader';
import { ThreeCircles } from 'react-loader-spinner';
// import {useAxios} from '../hooks/useAxios/useAxios';
// import prepareCartData from '../utils/prepareCartData';

function Cart(props) {
  const [cartList, setCartList] = useState([]);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const router = useRouter();
  const { ...state } = useAuthModalStore();
  const query = router.query;

  // props are Cart and CartActions mounted from redux store
  const  { cart, getActualCart, saveCart, updateCartRequest, removeFromCart } = props;
  const outOfStockList = useMemo(() => {
    return cartList.reduce((acc, cartItem) => {
      const product = cart.availableCartItems?.find(
        p => p.sku === cartItem.sku
      );
      if(!product) return acc;

      const itemStock = !product?.dd_available ? 0 : product.stock;

      if (+cartItem?.qty > +itemStock) {
        return [...acc, { name: cartItem.name, quantity: itemStock }];
      } else {
        return acc;
      }
    }, []);
  }, [cartList, cart.availableCartItems]);

  const saveTotalUnregistered = getSaveTotal(cartList);

  const handleCloseAlertModal = useCallback(async () => {
    const updatedItemsWithAvailability = cartList
      .filter(item => {
        if (!item.dd_available) {
          removeFromCart(item);
          return false; // Excluding unavailable elements
        }
        return true;
      })
      .map(item => {
        if (item.qty > item.stock) {
          if (!item.stock) {
            removeFromCart(item);
            return null; // Eliminate elements with zero remainder
          }
          return { ...item, qty: item.stock }; // Updating the quantity
        }
        return item;
      })
      .filter(Boolean);

    setCartList(updatedItemsWithAvailability);
    saveCart(updatedItemsWithAvailability);
    setIsAlertModalOpen(false);
  }, [cartList, cart, saveCart, setCartList]);

  // useEffect(() => {
  //   if (state?.isUserLogged && cart?.cartItems?.length === 0) {
  //     getActualCart();
  //   }
  // }, [state?.isUserLogged, cart?.cartItems]);

  useEffect(() => {
    setCartList(cart?.cartItems);

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);

  }, [cart.cartItems]);

  useEffect(() => {
    if (!isEmpty(outOfStockList) && state?.isUserLogged) {
      setIsAlertModalOpen(true);
    } else {
      setIsAlertModalOpen(false);
    }
  }, [outOfStockList, state?.isUserLogged]);

  return (
    <>
      <MetaHeader title={'Cart'} />
      <main className="main">
        {/*<SaleEndsIn endDate={endDate}/>*/}
        <div className="container">
          <ul className="checkout-progress-bar d-flex justify-content-center flex-wrap">
            <li className="active">
              <ALink href="/cart">Shopping Cart</ALink>
            </li>
            <li>
              <ALink href="/checkout">Checkout</ALink>
            </li>
            <li className="disabled">
              <ALink href="#">Order Complete</ALink>
            </li>
          </ul>

          {isLoading ? (
            <div className="row d-flex justify-content-center height-300">
              <ThreeCircles
                visible={true}
                height="100"
                width="100"
                color="#117a8b"
                ariaLabel="three-circles-loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            </div>
          ) : cartList.length === 0 ? (
            <div className="cart-table-container">
              <div className="table table-cart">
                <div className="cart-empty-page text-center">
                  <i className="icon-bag-2" />
                  <p>No products added to the cart</p>
                  <ALink
                    className="btn btn-dark btn-add-cart product-type-simple btn-shop font1"
                    href={{
                      pathname: '/shop',
                      query: {
                        ...query,
                        page: 1,
                        filters: JSON.stringify({
                          [sections.categories]: [],
                          [sections.price]: { min: 0, max: 0 },
                          [sections.materials]: [],
                          [sections.colors]: []
                        })
                      }
                    }}>
                    return to shop
                  </ALink>
                </div>
              </div>
            </div>
          ) : (
            <div className="row d-flex justify-content-center">
              <div className="col-11 cart-table-container">
                <table className="table table-cart">
                  <thead>
                    <tr>
                      <th className="product-col text-left">Product</th>
                      <th className="price-col text-left">Price</th>
                      <th className="qty-col">Quantity</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartList.map((item, index) => (
                      <React.Fragment key={'cart-item-mobile' + index}>
                        <tr className="product-row row-mobile">
                          <td className="col-4">
                            <div className="product-info-wrapper">
                              <figure className="product-image-container">
                                <ALink
                                  className="product-image"
                                  href={`/product/default${item.slug}`}>
                                  <img
                                    alt="product"
                                    src={
                                      item?.small_pictures?.length
                                        ? (!item?.small_pictures[0]?.url?.includes?.(
                                            'https:'
                                          )
                                            ? process.env.NEXT_PUBLIC_ASSET_URI
                                            : '') + item?.small_pictures[0]?.url
                                        : ''
                                    }
                                  />
                                </ALink>
                                <a
                                  className="btn-remove icon-cancel"
                                  href="#"
                                  onClick={e => {
                                    e.preventDefault();
                                    removeFromCart(item);
                                  }}
                                  title="Remove Product"
                                />
                              </figure>
                            </div>
                          </td>
                          {item.dd_available ? (
                            <td className="text-left col-8">
                              <div className="d-flex justify-content-end align-items-center">
                                <Qty
                                  disabled={cart.updating}
                                  max={item?.stock}
                                  onChangeQty={qty => updateCartRequest(item, qty)}
                                  value={item.qty}
                                />
                                <div className="d-flex flex-column align-items-center justify-content-center">
                                  <span className="subtotal-price ml-4">
                                    <b>
                                      ${(item.price[0] * item.qty)?.toFixed(2)}
                                    </b>
                                  </span>
                                  {item?.sale_end?.sale_in_seconds > 0 && (
                                    <span className="old-price product-home-old-price">
                                      {'$' + item?.price[1]?.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {item?.sale_end?.sale_in_seconds > 0 && (
                                <>
                                  <div className="product-home-sale">
                                    <LazyLoadImage
                                      alt="Hot Sale"
                                      src="/images/fire.svg"
                                      width={11}
                                    />
                                    <span className="product-home-sale-text">
                                      Sale Ends in:
                                      <span className="product-home-sale-text-lower">
                                        {/* need field 'promotionTo' & 'promotionFrom' from BE */}
                                        <span className="product-home-sale-text-bold">
                                          {item?.sale_end.days}
                                        </span>
                                        d{' '}
                                        <span className="product-home-sale-text-bold">
                                          {item?.sale_end.hours}
                                        </span>
                                        h{' '}
                                        <span className="product-home-sale-text-bold">
                                          {item?.sale_end.minutes}
                                        </span>
                                        m{' '}
                                      </span>
                                    </span>
                                  </div>
                                </>
                              )}
                              <h5 className="product-title">
                                <ALink href={`/product/default${item.slug}`}>
                                  {item.name} – SKU {item.sku}
                                </ALink>
                              </h5>
                            </td>
                          ) : (
                            <td>
                              <span style={{ color: 'red' }}>
                                Sorry, currently unavailable
                              </span>
                            </td>
                          )}
                        </tr>
                        <tr className="product-row row-desktop">
                          <td className="col-7">
                            <div className="product-info-wrapper">
                              <figure className="product-image-container">
                                <ALink
                                  className="product-image"
                                  href={`/product/default${item.slug}`}>
                                  <img
                                    alt="product"
                                    src={
                                      item?.small_pictures?.length
                                        ? (!item?.small_pictures[0]?.url?.includes?.(
                                            'https:'
                                          )
                                            ? process.env.NEXT_PUBLIC_ASSET_URI
                                            : '') + item?.small_pictures[0]?.url
                                        : ''
                                    }
                                  />
                                </ALink>

                                <a
                                  className="btn-remove icon-cancel"
                                  href="#"
                                  onClick={e => {
                                    e.preventDefault();
                                    removeFromCart(item);
                                  }}
                                  title="Remove Product"
                                />
                              </figure>

                              <h5 className="product-title">
                                <ALink href={`/product/default${item.slug}`}>
                                  {item.name} – SKU {item.sku}
                                </ALink>
                              </h5>
                            </div>
                          </td>
                          {item.dd_available ? (
                            <td className="cart-price">
                              {item?.sale_end?.sale_in_seconds > 0 ? (
                                <>
                                  {}
                                  <span className="product-price product-home-price">
                                    {'$' + item?.price[0]?.toFixed(2)}
                                  </span>{' '}
                                  <span className="old-price product-home-old-price">
                                    {'$' + item?.price[1]?.toFixed(2)}
                                  </span>
                                  <div className="product-home-sale">
                                    <LazyLoadImage
                                      alt="Hot Sale"
                                      src="/images/fire.svg"
                                      width={11}
                                    />
                                    <span className="product-home-sale-text">
                                      Sale Ends in:
                                      <span className="product-home-sale-text-lower">
                                        {/* need field 'promotionTo' & 'promotionFrom' from BE */}
                                        <span className="product-home-sale-text-bold">
                                          {item?.sale_end.days}
                                        </span>
                                        d{' '}
                                        <span className="product-home-sale-text-bold">
                                          {item?.sale_end.hours}
                                        </span>
                                        h{' '}
                                        <span className="product-home-sale-text-bold">
                                          {item?.sale_end.minutes}
                                        </span>
                                        m{' '}
                                      </span>
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <span className="product-price product-home-price">
                                  {'$' + item?.price[0]?.toFixed(2)}
                                </span>
                              )}
                            </td>
                          ) : (
                            <td>
                              <span style={{ color: 'red' }}>
                                Sorry, currently unavailable
                              </span>
                            </td>
                          )}
                          {item.dd_available ? (
                            <td>
                              <Qty
                                disabled={cart.updating}
                                max={item?.stock}
                                onChangeQty={qty => updateCartRequest(item, qty)}
                                value={item.qty}
                              />
                            </td>
                          ) : (
                            <td />
                          )}
                          {item.dd_available ? (
                            <td className="text-right">
                              <span className="subtotal-price">
                                <b>${(item.price[0] * item.qty)?.toFixed(2)}</b>
                              </span>
                            </td>
                          ) : (
                            <td />
                          )}
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr>
                      <td className="clearfix" colSpan="5" />
                    </tr>
                  </tfoot>
                </table>
                <div className="total-wrapper float-right">
                  <div className="lease-total">
                    <h4>Lease Total</h4>
                    <h4 style={{ color: '#000' }}>
                      ${getCartTotal(cartList)?.toFixed(2)}
                    </h4>
                  </div>

                  <div className="save-total">
                    <h4>Saved</h4>
                    {!state.isUserLogged && (
                      <h4>
                        {saveTotalUnregistered > 0
                          ? '-$' + saveTotalUnregistered?.toFixed(2)
                          : '$' + (0).toFixed(2)}
                      </h4>
                    )}

                    {state.isUserLogged && (
                      <h4>
                        {cart?.saved_amount?.toFixed(2) > 0
                          ? '-$' + cart?.saved_amount?.toFixed(2)
                          : '$' + (0).toFixed(2)}
                      </h4>
                    )}
                  </div>

                  <ALink
                    className="btn btn-block btn-dark font1 bold"
                    href="checkout">
                    CHECKOUT
                  </ALink>
                </div>

                <div className="input-link">
                  <ALink
                    className="product-image "
                    href={{
                      pathname: '/shop',
                      query: {
                        ...query,
                        filters: JSON.stringify({
                          [sections.categories]: [],
                          [sections.price]: { min: 0, max: 0 },
                          [sections.materials]: [],
                          [sections.colors]: []
                        })
                      }
                    }}>
                    Continue Shopping
                  </ALink>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mb-6" />

        <CartAlertModal
          isOpen={isAlertModalOpen}
          productsList={outOfStockList}
          setIsOpen={handleCloseAlertModal}
        />
      </main>
    </>
  );
}

Cart.propTypes = {
  cart: propTypes.object,
  getActualCart: propTypes.func,
  removeFromCart: propTypes.func,
  saveCart: propTypes.func,
  updateCartRequest: propTypes.func
};

const mapStateToProps = state => {
  return {
    cart: state.cart ? state.cart : {}
  };
};

export default connect(mapStateToProps, { ...CartAction })(Cart);
